import {useCallback, useMemo} from 'react';
import {useFormikContext} from 'formik';
import {DrawerFields} from 'components/customer/Product/entity/Drawer';
import {DrawerParameters} from 'components/customer/Product/entity/DrawerParameters';
import {
    FaceRange,
    faceFloor,
    faceRange,
} from 'components/customer/Product/Drawer/helpers';
import {MIN_DRAG_HEIGHT} from 'components/customer/Product/Drawer/helpers/useDragGesture';
import {useDrawerDefaults} from 'components/customer/Product/Drawer/DrawerDefaultsContext';
import {useGetDrawerSystemQuery} from 'components/customer/Product/store/drawerRunnerApi';

// The heights each face of a drawer cabinet can take, the same for typing as
// for dragging
export const useFaceRange = (drawerParams: DrawerParameters) => {
    const {values} = useFormikContext<DrawerFields>();
    const {shortestRunner} = useDrawerDefaults();
    const {data: drawerSystems} = useGetDrawerSystemQuery();

    const drawers = values.drawers;
    const typeField = drawerParams.drawerTypeFieldName;
    const faceHeightField = drawerParams.drawerFaceHeightFieldName;
    const styleField = drawerParams.drawerStyleFieldName;

    const floors = useMemo(
        () =>
            (drawers ?? []).map((drawer) =>
                Math.max(
                    MIN_DRAG_HEIGHT,
                    faceFloor(drawer, drawerSystems, typeField, shortestRunner)
                )
            ),
        [drawers, drawerSystems, typeField, shortestRunner]
    );

    const rangeFor = useCallback(
        (index: number): FaceRange =>
            faceRange(
                drawers ?? [],
                index,
                faceHeightField,
                styleField,
                floors
            ),
        [drawers, faceHeightField, styleField, floors]
    );

    // Why a typed height is turned away, or null when it fits; worded like the
    // product's other face errors, as the save lists them all together. Only
    // the room left for the autos: a face too short for itself is already
    // flagged by its runner and door rules, with the door's own minimum.
    const check = useCallback(
        (index: number, height: number) => {
            const {max} = rangeFor(index);

            return height > max
                ? `Drawer face #${index + 1} must be less than or equal to ${max}mm`
                : null;
        },
        [rangeFor]
    );

    return {rangeFor, check};
};
